import DepthTrack from '../../components/log-viewer/depth-track';
import useWindowSize from '../../components/useWindowResize';
import './trackData.scss';
import getConfig from '../../app/config';
import auth from '../../components/auth';
import { useEffect, useRef, useState, useMemo } from 'react';
import Tracks from './Tracks';
import StratigraphicSurface from '../../components/log-viewer/stratigraphic-surface';
import VerticalRangeSelector from '../../components/log-viewer/vertical-range-selector';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { selectTrackDataCache, selectIsLoadingHighResolution } from '../borehole/resolutionSelectors';

export default function TrackData(props : any) {

    const { 
      clientId, 
      projectId, 
      boreholeId, 
      startDepth, 
      endDepth, 
      templateId, 
      byBoreholeDepth,
      depthType,
      trackData,
      sortedTrackData,
      sortedGroupTrackData,
      groupTrackData,
      template,
      showView,
      view,
      updateCurve,
      updateCurves,      
      setShowGridlines,
      setScaleType,
      setTrackWidth,
      setViewTrackWidth,
      depthUnit,
      removeTrack,      
      trackTypes,
      editMode,
      depthMajorIntervals,
      setDepthMajorIntervals,
      showDepthGridLines,
      setShowDepthGridlines,
      showDepthMinorIntervals,
      setShowDepthMinorIntervals,
      depthMinorIntervals,
      setDepthMinorIntervals,
      deletedCurves,
      selectedTrackCategory,
      setSelectedTrackCategory,
      trackHeaderClick,
      setMetaDataDepth,
      deselectAllTracks,
      selectLithology,
      addLithology,
      collapseGroup,
      setStartDepth,
      setEndDepth,
      minDepth,
      maxDepth, 
      setPauseUpdate,
      trackDataLoading,
      mouseWheelZoom,
      scalesLoaded,
      scaleMetadata,
      scaleMetadataByBorehole,
      setManualDepthRange
     } = props;

    // Prefer sortedTrackData (template tracks with curves) for header-height calculations.
    // Raw trackData items are per-trackTypeId data without a `curves` array, so maxGraphCurves
    // would always evaluate to 0 if they were used here, causing minHeaderHeight to be too small
    // for multi-curve tracks.
    const sTrackData = (sortedTrackData && sortedTrackData.length > 0) ? sortedTrackData : ((trackData && trackData.length) ? trackData : []);
    // For group data, prioritize sortedGroupTrackData if it has properly formatted groups
    const sGroupTrackData = (sortedGroupTrackData && sortedGroupTrackData.length > 0 && sortedGroupTrackData[0]?.tracks) 
      ? sortedGroupTrackData 
      : ((groupTrackData && groupTrackData.length) ? groupTrackData : []);

    const windowSize: any = useWindowSize();
    const trackDataCache = useSelector((state: RootState) => selectTrackDataCache(state));
    const isLoadingHighResolution = useSelector((state: RootState) => selectIsLoadingHighResolution(state));
    const container: any = useRef();
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);  
    const [isPanning, setIsPanning] = useState(false);
    
    // Track last fetch parameters to prevent redundant fetches
    const lastFetchParamsRef = useRef<string>('');

  // ...existing code...

  function debounce(func: any, wait: any, immediate: any) {
    var timeout: any;
    return function (this: any) {
      var context : any = this,
        args: any = arguments
      var later = function () {
        timeout = null
        if (!immediate) func.apply(context, args)
      }
      var callNow = immediate && !timeout
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
      if (callNow) func.apply(context, args)
    }
  }

  useEffect(() => {
    const handleWindowResize = debounce(function handleResize() {
      setWindowHeight(window.innerHeight);
    }, 250, null);
    window.addEventListener("resize", handleWindowResize);

    // Return a function from the effect that removes the event listener
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const chartHeight = 400; // Example height of your chart area

  // Compute current slider percentages from depth state.
  // With no debounce these update on every mouse-move re-render, keeping the slider fully responsive.
  let startDepthPercent = startDepth != null ? (startDepth - minDepth) / (maxDepth - minDepth) * 100 : 0;
  if (startDepthPercent < 0) {
    startDepthPercent = 0;
  } 
  
  let endDepthPercent = endDepth != null ? (endDepth - minDepth) / (maxDepth - minDepth) * 100 : 100;
  if (endDepthPercent > 100) {
    endDepthPercent = 100;
  }

  const handleRangeChange = (startPercent: any, endPercent: any) => {    
    if (minDepth == null || maxDepth == null) {
      console.warn("minDepth or maxDepth is null, cannot update start and end depths.");
      return;
    }
    const newStartDepth = minDepth + (startPercent / 100) * (maxDepth - minDepth);
    const newEndDepth = minDepth + (endPercent / 100) * (maxDepth - minDepth);
    setStartDepth(Math.round(newStartDepth * 1000) / 1000);
    setEndDepth(Math.round(newEndDepth * 1000) / 1000);
  };

  const handleSetIsPanning = (isPanning: boolean) => {
    // This function can be used to set a state indicating whether the range selector is being dragged      
    setIsPanning(isPanning);
    if (setPauseUpdate) {      
      setPauseUpdate(isPanning);
    }
  };

  // Derive the set of visible trackTypeIds for all tracks (including non-numeric)
  const visibleTrackTypeIds: number[] = useMemo(() => {
    const ids = new Set<number>();
    const collectFromTracks = (tracks: any[], _source: string) => {
      tracks?.forEach((track: any) => {
        if (typeof track?.trackTypeId === 'number') {
          ids.add(track.trackTypeId);
        }
        if (typeof track?.textTrackTypeId === 'number') {
          ids.add(track.textTrackTypeId);
        }
        if (typeof track?.lithologyTypeId === 'number') {
          ids.add(track.lithologyTypeId);
        }
        if (typeof track?.stratigraphyTrackTypeId === 'number') {
          ids.add(track.stratigraphyTrackTypeId);
        }

        // Also collect from curves if present
        if (track?.curves && Array.isArray(track.curves)) {
          track.curves.forEach((c: any) => {
            if (typeof c?.trackTypeId === 'number') {
              ids.add(c.trackTypeId);
            }
          });
        }
      });
    };

    if (showView && Array.isArray(sGroupTrackData)) {
      sGroupTrackData.forEach((g: any, gIndex: number) => collectFromTracks(g.tracks ?? [], `group ${gIndex} (${g.groupName})`));
    } else if (Array.isArray(sTrackData)) {
      collectFromTracks(sTrackData, 'sortedTrackData');
    }

    return Array.from(ids);
  }, [showView, sortedGroupTrackData, sortedTrackData, trackData, groupTrackData, depthType]);

  // Create a stable string representation of track type IDs for dependency tracking
  const visibleTrackTypeIdsKey = useMemo(() => {
    return visibleTrackTypeIds.sort((a, b) => a - b).join(',');
  }, [visibleTrackTypeIds]);

  // Ensure resolution metadata is available for visible track types
  // useEffect(() => {
  //   if (!clientId || !projectId || !boreholeId) return;
  //   if (!visibleTrackTypeIds || visibleTrackTypeIds.length === 0) return;

  //   let trackTypeIds: number[] = [];
  //   visibleTrackTypeIds.forEach((trackTypeId) => {
  //     const existing = resolutionMetadataByTrackType && resolutionMetadataByTrackType[trackTypeId];
  //     if (!existing) {
  //       trackTypeIds.push(trackTypeId);
  //     }
  //   });

  //   dispatch(getScaleMetadata({ clientId, projectId, boreholeId, trackTypeIds }));
  // }, [clientId, projectId, boreholeId, visibleTrackTypeIds, resolutionMetadataByTrackType, dispatch]);

  // Compute overall visible depth window (covers groups if showView)
  const { overallStartDepth, overallEndDepth } = useMemo(() => {

    if (!byBoreholeDepth) {
      //debugger;
    }
    if (startDepth == null || endDepth == null) {
      return { overallStartDepth: null as number | null, overallEndDepth: null as number | null };
    }

    if (!showView || !Array.isArray(sGroupTrackData) || sGroupTrackData.length === 0) {
      return { overallStartDepth: startDepth, overallEndDepth: endDepth };
    }

    // The depth track is relative to the FIRST group's coordinate system
    // Calculate all offsets relative to the first group's topOffset
    const firstGroupTopOffset = sGroupTrackData[0]?.topOffset ?? 0;
    // Use the actual absolute depth of the aligning surface if available (from stratigraphyLexiconStartAndEnds)
    const aligningLexiconId = view?.topOffsetStratigraphyLexiconId;
    const getAlignmentDepthForOverall = (g: any): number => {
      if (Number(g.topOffsetType) === 2) return g.topOffset ?? 0;
      const entry = g.stratigraphyLexiconStartAndEnds
        ?.find((e: any) => e.stratigraphyLexiconId === aligningLexiconId);
      if (entry?.startDepth != null) return entry.startDepth;
      return g.topOffset ?? 0; // fallback: shift = topOffset(this) - topOffset(first)
    };
    const firstGroupAlignmentDepth = getAlignmentDepthForOverall(sGroupTrackData[0]);

    // Approximate unified window to cover all groups based on their topOffset
    let minStart = Number.POSITIVE_INFINITY;
    let maxEnd = Number.NEGATIVE_INFINITY;

    sGroupTrackData.forEach((group: any, index: number) => {
      const normalizedOffset = view?.topOffsetShowLine
        ? 0
        : getAlignmentDepthForOverall(group) - firstGroupAlignmentDepth;
      const groupStart = startDepth + normalizedOffset;
      const groupEnd = endDepth + normalizedOffset;
      
      if (groupStart < minStart) minStart = groupStart;
      if (groupEnd > maxEnd) maxEnd = groupEnd;
    });

    if (!isFinite(minStart) || !isFinite(maxEnd)) {
      return { overallStartDepth: startDepth, overallEndDepth: endDepth };
    }
    

    return { overallStartDepth: minStart, overallEndDepth: maxEnd };
  }, [showView, sortedGroupTrackData, groupTrackData, startDepth, endDepth, view, byBoreholeDepth, depthType]);

  
    const apiUrl = getConfig().apiUrl;

    const imageAuthToken = auth.getTokenForImages();
    
    const onUpdateCurve = (templateTrackId: string, curve: any) => {
      updateCurve(templateTrackId, curve);
    } 

    const onUpdateCurves = (templateTrackId: string, curves: any) => {
      updateCurves(templateId, templateTrackId, curves);
    }

    const onSetShowGridlines = (templateTrackId: string, trackTypeId: any, show: boolean) => {
      setShowGridlines(templateId, templateTrackId, trackTypeId, show);
    }

    const onSetScaleType = (templateTrackId: string, trackTypeId: any, scaleType: number) => {
      setScaleType(templateId, templateTrackId, trackTypeId, scaleType);
    }
    
    const onSetTrackWidth = (templateTrackId: string, width: number) => {
      setTrackWidth(templateId, templateTrackId, width);
    }

    const onSetViewTrackWidth = (groupId: any, trackId: string, width: number) => {
      setViewTrackWidth(groupId, trackId, width);
    }
    
    const onGroupTrackHeaderClick = (track: any, groupId: any) => {      
      trackHeaderClick(track, groupId)
    };

    const cumulativeHeaderHeight = 20.86;
    const headerHeight = 67;
    const headerBottomPadding = 13;
    const imageWithoutLegendHeaderHeight = 54;
    const imageWithLegendHeaderHeight = 65;
    
    // Only count graph-type tracks (type 0 / type 8) for the headerHeight-based calculation.
    // Cumulative (3), tadpole (4), text (7) and stratigraphy (9) use a different, smaller header
    // height and are accounted for separately below.
    const tracksWithCurves = sTrackData.filter((track: any) => (track.trackType === 0 || track.trackType === 8) && track.curves?.length > 0 && track?.datas?.some((data: any) => !data.noData));
    let maxGraphCurves = sTrackData ? tracksWithCurves.length > 0 ? Math.max(...tracksWithCurves.map((track: any) => track?.curves?.length ?? 0)) : 0 : 0;
    let maxCumulativeCurves = sTrackData ? Math.max(...sTrackData.filter((track: any) => track.trackType === 3 && track?.datas?.some((data: any) => !data.noData)).map((track: any) => track?.curves?.length ?? 0)) : 0;
    let maxLithologyCodes = sTrackData ? Math.max(...sTrackData.filter((track: any) => track.trackType === 5).map((track: any) => track?.codesAndColors.length ?? 0)) : 0;

    if (showView) {
      sGroupTrackData?.filter((group: any) => group.tracks).forEach((group: any) => {        
        const groupMasGraphCurves = Math.max(...group.tracks.filter((track: any) => track && (track.trackType === 0 || track.trackType === 8) && track.curves?.length > 0 && track?.datas?.some((data: any) => !data.noData)).map((track: any) => track?.curves?.length ?? 0));
        if (groupMasGraphCurves > maxGraphCurves) {
          maxGraphCurves = groupMasGraphCurves;
        }

        const groupMaxCumulativeCurves = Math.max(...group.tracks.filter((track: any) => track && track.trackType === 3 && track?.datas?.some((data: any) => !data.noData)).map((track: any) => track?.curves?.length ?? 0));
        if (groupMaxCumulativeCurves > maxCumulativeCurves) {
          maxCumulativeCurves = groupMaxCumulativeCurves;
        }

        const groupMaxLithologyCodes = Math.max(...group.tracks.filter((track: any) => track &&  track.trackType === 5).map((track: any) => track?.codesAndColors.length ?? 0));
        if (groupMaxLithologyCodes > maxLithologyCodes) {
          maxLithologyCodes = groupMaxLithologyCodes;
        }
      });
    }
    
    const containsImageWithLegend = sTrackData?.some((track: any) => track.trackType === 1 && track.legend && !track.legendRollover);
        
    if (maxCumulativeCurves < 0) {
      maxCumulativeCurves = 0;
    }

    let minHeaderHeight = 0;
    if (containsImageWithLegend) {
      minHeaderHeight = imageWithLegendHeaderHeight;
    } else {
      minHeaderHeight = imageWithoutLegendHeaderHeight;
    }

    const curveHeights = (maxGraphCurves * headerHeight) - headerBottomPadding;
    if (curveHeights > minHeaderHeight) {
      minHeaderHeight = curveHeights;
    }

    const minCumulativeHeaderHeight = (maxCumulativeCurves * cumulativeHeaderHeight);

    const minLithologyHeaderHeight = (maxLithologyCodes * cumulativeHeaderHeight) + 19.2;
    
    if (minCumulativeHeaderHeight > minHeaderHeight) {
      minHeaderHeight = minCumulativeHeaderHeight;
    }

    if (minLithologyHeaderHeight > minHeaderHeight) {
      minHeaderHeight = minLithologyHeaderHeight;
    }
       
    const topMargin = template?.showValueAxisAnnotation ? 35 : 10;

    const trackTop = minHeaderHeight;// + topMargin;
    let imageHeaderHeight = 0;
    const imageCurveHeaderHeight = (minHeaderHeight + 123);
    if (containsImageWithLegend) {          
      imageHeaderHeight = imageWithLegendHeaderHeight + 125;
    } else {
      imageHeaderHeight = imageWithoutLegendHeaderHeight + 125;
    }

    if (imageCurveHeaderHeight > imageHeaderHeight) {
      imageHeaderHeight = imageCurveHeaderHeight;
    }
    
    const boreholeLabelHeight = 35;
    const imageHeight = windowSize.height - imageHeaderHeight - topMargin - (showView ? boreholeLabelHeight : 0);
    const availableHeight = windowHeight - 56 - 57 - (showView ? boreholeLabelHeight : 0);
    const depthTrackAvailableHeight = windowHeight - 56 - 57;
    const depthTrackTopMargin = topMargin + (showView ? boreholeLabelHeight : 0);
 

  // TrackData no longer performs its own data fetching.


  // Guard early return after all hooks are declared
  if (!trackData) {
    return (
      <div style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: '#fff',
        padding: '12px 16px',
        borderRadius: '4px',
        fontSize: '12px',
        maxWidth: '420px',
        lineHeight: 1.4
      }}>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>TrackData debug (no trackData)</div>
        <div>showView: {String(showView)}</div>
        <div>trackDataLoading: {String(trackDataLoading)}</div>
        <div>scalesLoaded: {String(scalesLoaded)}</div>
        <div>clientId/projectId/boreholeId: {String(clientId)} / {String(projectId)} / {String(boreholeId)}</div>
        <div>startDepth/endDepth: {String(startDepth)} / {String(endDepth)}</div>
        <div>overallStartDepth/overallEndDepth: {String(overallStartDepth)} / {String(overallEndDepth)}</div>
        <div>visibleTrackTypeIdsKey: {visibleTrackTypeIdsKey || '(empty)'}</div>
        <div>sortedGroupTrackData length: {String(sortedGroupTrackData?.length || 0)}</div>
        <div>groupTrackData length: {String(groupTrackData?.length || 0)}</div>
        <div>scaleMetadata length: {String(scaleMetadata?.length || 0)}</div>
        <div>scaleMetadataByBorehole keys: {String(Object.keys(scaleMetadataByBorehole || {}).length)}</div>
        <div>trackDataCache keys: {String(Object.keys(trackDataCache || {}).length)}</div>
      </div>
    );
  }

const onZoom = (zoomFactor: number, mouseY: number) => {
  if (startDepth == null || endDepth == null || minDepth == null || maxDepth == null) {
    return;
  }

  const chartHeight = imageHeight;
  if (chartHeight <= 0) return;

  const currentVisibleRange = endDepth - startDepth;
  const mouseYWithoutHeader = mouseY - minHeaderHeight - topMargin;

  if (mouseYWithoutHeader < 0 || mouseYWithoutHeader > chartHeight) {
    return;
  }

  const depthAtCursor = startDepth + (mouseYWithoutHeader / chartHeight) * currentVisibleRange;

  // Determine the new visible range, constrained by min and max possible zoom
  const totalDataRange = maxDepth - minDepth;
  const minAllowedVisibleRange = 0.01; // Smallest allowed zoom window
  let newVisibleRange = currentVisibleRange * zoomFactor;
  if (newVisibleRange < minAllowedVisibleRange) {
    newVisibleRange = minAllowedVisibleRange;
  }
  if (newVisibleRange > totalDataRange) {
    newVisibleRange = totalDataRange;
  }

  // Calculate the new start depth based on the cursor position
  const ratioAboveCursor = (depthAtCursor - startDepth) / currentVisibleRange;
  let newMinDepth = depthAtCursor - newVisibleRange * ratioAboveCursor;
  let newMaxDepth = newMinDepth + newVisibleRange;

  // Adjust the window if it exceeds the absolute boundaries
  if (newMinDepth < minDepth) {
    newMinDepth = minDepth;
    newMaxDepth = newMinDepth + newVisibleRange;
  }
  if (newMaxDepth > maxDepth) {
    newMaxDepth = maxDepth;
    newMinDepth = newMaxDepth - newVisibleRange;
    // After shifting, re-check that we haven't pushed the start below the minimum
    if (newMinDepth < minDepth) {
        newMinDepth = minDepth;
    }
  }

  // Final check for equality to prevent unnecessary re-renders if nothing changed
  if (newMinDepth === startDepth && newMaxDepth === endDepth) {
    return;
  }

  // Apply the new depths
  setStartDepth(newMinDepth);
  setEndDepth(newMaxDepth);

  // Mark as manual depth range to prevent auto-reset when data loads
  if (setManualDepthRange) {
    setManualDepthRange(true);
  }

  // Update the range selector component
  handleRangeChange(
    ((newMinDepth - minDepth) / totalDataRange) * 100,
    ((newMaxDepth - minDepth) / totalDataRange) * 100
  );
};

    /**
   * Pans the depth view based on a vertical pixel delta.
   * This function contains the logic to convert pixels to depth.
   */
  const onPan = (pixelDeltaY: number) => {    
    if (startDepth == null || endDepth == null || minDepth == null || maxDepth == null) {
      return;
    }
    const chartHeight = imageHeight;
    if (chartHeight <= 0) return;

    const visibleRange = endDepth - startDepth;
    const depthPerPixel = visibleRange / chartHeight;
    const depthChange = pixelDeltaY * depthPerPixel;

    let newStartDepth = startDepth + depthChange;
    let newEndDepth = endDepth + depthChange;

//    console.log(`Panning: pixelDeltaY=${pixelDeltaY}, depthPerPixel=${depthPerPixel}, depthChange=${depthChange}, newStartDepth=${newStartDepth}, newEndDepth=${newEndDepth}`);

    // Constrain panning within the overall min/max depth boundaries
    if (newStartDepth < minDepth) {
      newStartDepth = minDepth;
      newEndDepth = minDepth + visibleRange;
    } else if (newEndDepth > maxDepth) {
      newEndDepth = maxDepth;
      newStartDepth = maxDepth - visibleRange;
    }

    const finalStartDepth = parseFloat(newStartDepth.toFixed(4));
    const finalEndDepth = parseFloat(newEndDepth.toFixed(4));

    if (finalStartDepth !== startDepth || finalEndDepth !== endDepth) {
      setStartDepth(finalStartDepth);
      setEndDepth(finalEndDepth);
    } else {
  //    console.warn("Panning did not change depth range, no update made.");
    }
  };

    return (        
        <>
           <div className="log-track-container">
            
            {true &&
            <>
            <div style={{ marginTop: (minHeaderHeight + topMargin + (showView ? boreholeLabelHeight : 0)) + "px" }}>
              <VerticalRangeSelector
                      containerHeight={imageHeight}
                      minRangePercent={0.01}
                      onRangeChange={handleRangeChange}
                      setIsDragging={handleSetIsPanning}
                      disabled={trackDataLoading}                      
                      startDepth={minDepth}
                      endDepth={maxDepth}
                      startPercent={startDepthPercent}
                      endPercent={endDepthPercent}                      
                      depthUnit={depthUnit} 
                      minHeaderHeight={minHeaderHeight}
                      headerBottomPadding={headerBottomPadding}                      
                      availableHeight={depthTrackAvailableHeight}
                      trackTop={trackTop}
                      topMargin={depthTrackTopMargin}
                      depthMajorIntervals={depthMajorIntervals} 
                      setDialogMajorIntervals={setDepthMajorIntervals}
                      showDepthGridLines={showDepthGridLines}
                      setShowDepthGridlines={setShowDepthGridlines}
                      depthMinorIntervals={depthMinorIntervals}
                      setDepthMinorIntervals={setDepthMinorIntervals}
                      showDepthMinorIntervals={showDepthMinorIntervals}
                      setShowDepthMinorIntervals={setShowDepthMinorIntervals}      
                    />
             </div>

            {startDepth != null && endDepth != null && (view?.topOffsetShowLine || !view?.topOffsetStratigraphyLexiconId || view?.topOffsetShowLine === null) && <DepthTrack 
                startDepth={startDepth} 
                endDepth={endDepth} 
                depthUnit={depthUnit} 
                minHeaderHeight={minHeaderHeight}
                headerBottomPadding={headerBottomPadding}
                editMode={editMode}      
                availableHeight={depthTrackAvailableHeight}
                trackTop={trackTop}
                topMargin={depthTrackTopMargin}
                depthMajorIntervals={depthMajorIntervals} 
                setDialogMajorIntervals={setDepthMajorIntervals}
                showDepthGridLines={showDepthGridLines}
                setShowDepthGridlines={setShowDepthGridlines}
                depthMinorIntervals={depthMinorIntervals}
                setDepthMinorIntervals={setDepthMinorIntervals}
                showDepthMinorIntervals={showDepthMinorIntervals}
                setShowDepthMinorIntervals={setShowDepthMinorIntervals}
                deselectAllTracks={deselectAllTracks}
                showValueAxisAnnotation={template?.showValueAxisAnnotation} /> }

              <div ref={container} className="columns flex-container" style={{ overflowY: "hidden", flexGrow: showView ? 1: 0 }}>

              {showView && <>
                {sGroupTrackData?.map((group: any, index: number) => {  
                  let minWidth = 150;                      
                  const tracksWithData = group.tracks?.filter((track: any) => track && (track.noData == false || track.noImage == false))
                  if (tracksWithData?.length > 0) {
                    minWidth = tracksWithData[0].trackWidth - 6;
                  }

                  // work out the start and end depth for the group
                  // The depth track is relative to the FIRST group's coordinate system
                  // Calculate all offsets relative to the first group's topOffset
                  const firstGroupTopOffset = sGroupTrackData[0]?.topOffset ?? 0;
                  const maxGroupTopOffset = Math.max(...sGroupTrackData.map((group: any) => group.topOffset || 0));
                  const maxGroupBottomOffset = Math.max(...sGroupTrackData.map((group: any) => maxGroupTopOffset - group.topOffset || 0));
                  

const aligningLexiconId = view?.topOffsetStratigraphyLexiconId;

// Compute the absolute alignment depth for a given group.
// Manual mode: user-entered depth (stored in topOffset).
// Lexicon mode: actual surface depth from stratigraphyLexiconStartAndEnds,
//               falling back to the relative topOffset offset (relative to first group) then to topOffset itself.
const getAlignmentDepth = (g: any): number => {
  if (Number(g.topOffsetType) === 2) {
    return g.topOffset ?? 0; // manual: absolute depth entered by user
  }
  const entry = g.stratigraphyLexiconStartAndEnds
    ?.find((e: any) => e.stratigraphyLexiconId === aligningLexiconId);
  if (entry?.startDepth != null) {
    return entry.startDepth; // lexicon: absolute depth from data
  }
  // Fallback (data not yet loaded): return topOffset so that
  // shift = topOffset(this) - topOffset(first), the old relative formula
  return g.topOffset ?? 0;
};

const firstGroupAlignmentDepth = getAlignmentDepth(sGroupTrackData[0]);

                  // find the maximum offset to the bottom of the group
                  const maxGroupEndDepth = Math.max(...sGroupTrackData.map((group: any) => group.maximumDepth - group.topOffset || 0));

                  // the maximum end depth needed to fit in all the groups is maxGroupTopOffset + maxGroupEndDepth
                  const maximumEndDepth = maxGroupTopOffset + maxGroupEndDepth;

                  let groupStartDepth = view?.topOffsetShowLine ? startDepth : startDepth - (maxGroupTopOffset - group.topOffset);
                  
                  //const groupEndDepth = view.topOffsetShowLine ? endDepth : endDepth - (maxGroupTopOffset - group.topOffset);
                  //const groupEndDepth = endDepth + group.topOffset - maxGroupBottomOffset;// + group.topOffset - maxGroupTopOffset;
                  //const groupEndDepth = endDepth + group.topOffset - maxGroupBottomOffset;// + group.topOffset - maxGroupTopOffset;
                  let groupEndDepth = maximumEndDepth + groupStartDepth;// + group.topOffset - maxGroupTopOffset;
                  //const groupEndDepth = endDepth + maxGroupBottomOffset;// + group.topOffset - maxGroupTopOffset;

                  if (index === 0) {
                    // First group: no offset (it's the reference)
                    groupStartDepth = startDepth; 
                    groupEndDepth = endDepth;
                  } else {
                    // Unified: shift = thisGroupAlignmentDepth - firstGroupAlignmentDepth
                    // Works for all combinations of manual/lexicon on any group.
                    const groupTopOffset = view?.topOffsetShowLine
                      ? 0
                      : getAlignmentDepth(group) - firstGroupAlignmentDepth;
                    groupStartDepth = startDepth + groupTopOffset;
                    groupEndDepth = endDepth + groupTopOffset;
                  }        
                                    
                const totalTrackWidth = group.tracks?.reduce((sum: any, track: any) => {                  
                  return sum + track.trackWidth;
                }, 0);                

                  return (
                    <>
                    {index > 0 && view?.topOffsetStratigraphyTrackTypeId && startDepth != null && endDepth != null && view?.topOffsetShowLine && 
                    <StratigraphicSurface                         
                        startDepth={groupStartDepth} 
                        endDepth={groupEndDepth} 
                        depthUnit={depthUnit} 
                        minHeaderHeight={minHeaderHeight}
                        headerBottomPadding={headerBottomPadding}                        
                        availableHeight={depthTrackAvailableHeight}
                        trackTop={trackTop}
                        topMargin={depthTrackTopMargin}
                        leftStratigraphyLexiconStartAndEnds={sortedGroupTrackData[index - 1]?.stratigraphyLexiconStartAndEnds}
                        rightStratigraphyLexiconStartAndEnds={group.stratigraphyLexiconStartAndEnds}
                        correlations={sortedGroupTrackData[index - 1]?.correlations} /> 
                    }

                    {view?.topOffsetStratigraphyLexiconId && startDepth != null && endDepth != null && view?.topOffsetShowLine === false && 
                      <DepthTrack 
                        startDepth={groupStartDepth} 
                        endDepth={groupEndDepth} 
                        depthUnit={depthUnit} 
                        minHeaderHeight={minHeaderHeight}
                        headerBottomPadding={headerBottomPadding}
                        editMode={editMode}      
                        availableHeight={depthTrackAvailableHeight}
                        trackTop={trackTop}
                        topMargin={depthTrackTopMargin}
                        depthMajorIntervals={depthMajorIntervals} 
                        setDialogMajorIntervals={setDepthMajorIntervals}
                        showDepthGridLines={showDepthGridLines}
                        setShowDepthGridlines={setShowDepthGridlines}
                        depthMinorIntervals={depthMinorIntervals}
                        setDepthMinorIntervals={setDepthMinorIntervals}
                        showDepthMinorIntervals={showDepthMinorIntervals}
                        setShowDepthMinorIntervals={setShowDepthMinorIntervals}
                        deselectAllTracks={deselectAllTracks}
                        showValueAxisAnnotation={template?.showValueAxisAnnotation} />
                    }
                  
                  <div key={group.groupId} className="column flex-item group-outline" style={{maxWidth: group.collapsed ? minWidth + "px" : totalTrackWidth + "px", marginRight: view?.topOffsetStratigraphyTrackTypeId ? "0px" : "5px" }}>
                    <div className='group-header-container' style={{backgroundColor: group.color, minHeight: boreholeLabelHeight }}>
                      <div className='group-viewer-header' style={{backgroundColor: group.color }}>{group.groupName}{' '}
                        {group.boreholeDoesNotContainStratigraphy && view?.topOffsetStratigraphyLexiconId && <span
                          className="material-icons group-header-icon"
                          title={'Borehole does not contain stratigraphy data for the selected lexicon.'}
                        >
                          warning
                        </span>}
                      </div>
                      <div className='group-header-arrow-container'><button className='button group-header-button' style={{backgroundColor: group.color}} onClick={() => collapseGroup(group.groupId)}>{group.collapsed ? <img src="/images/icons/Atlas Close Icon Black.svg"></img> : <img src="/images/icons/Atlas Open Icon Black.svg"></img>}</button></div>
                    </div>
                    <div ref={container} className="columns flex-container" style={{ overflow: "hidden" }}>
                    <Tracks
                      clientId={clientId}
                      projectId={projectId}
                      boreholeId={boreholeId}
                      // startDepth={startDepth + group.topOffset - 100}
                      // endDepth={endDepth + group.topOffset - 100}
                      startDepth={groupStartDepth}
                      endDepth={groupEndDepth}
                      byBoreholeDepth={byBoreholeDepth}
                      sortedTrackData={group.tracks}
                      template={template}
                      depthUnit={depthUnit}
                      removeTrack={removeTrack}
                      trackTypes={trackTypes}
                      editMode={editMode}
                      depthMajorIntervals={depthMajorIntervals}
                      showDepthGridLines={showDepthGridLines}
                      showDepthMinorIntervals={showDepthMinorIntervals}
                      depthMinorIntervals={depthMinorIntervals}
                      deletedCurves={deletedCurves}
                      selectedTrackCategory={selectedTrackCategory}
                      setSelectedTrackCategory={setSelectedTrackCategory}
                      trackHeaderClick={(track: any) => onGroupTrackHeaderClick(track, group.groupId)}
                      setMetaDataDepth={setMetaDataDepth}
                      selectLithology={selectLithology}
                      addLithology={addLithology}
                      topMargin={topMargin}
                      availableHeight={availableHeight}
                      onUpdateCurve={onUpdateCurve}
                      onUpdateCurves={onUpdateCurves}
                      onSetShowGridlines={onSetShowGridlines}
                      onSetScaleType={onSetScaleType}                      
                      onSetTrackWidth={(trackId: any, width: number) => onSetViewTrackWidth(group.groupId, trackId, width)}
                      headerHeight={headerHeight}
                      minHeaderHeight={minHeaderHeight}
                      headerBottomPadding={headerBottomPadding}
                      windowSize={windowSize}
                      container={container}
                      imageHeight={imageHeight}
                      apiUrl={apiUrl}
                      imageAuthToken={imageAuthToken}                  
                      cumulativeHeaderHeight={cumulativeHeaderHeight}
                      onZoom={onZoom}
                      onPan={onPan}
                      onSetIsPanning={handleSetIsPanning}
                      isPanning={isPanning}
                      mouseWheelZoom={mouseWheelZoom}
                    />
                    </div>
                  </div>
                  </>)
                })}
              </> }
              {!showView && <>                
                  <Tracks
                    clientId={clientId}
                    projectId={projectId}
                    boreholeId={boreholeId}
                    startDepth={startDepth}
                    endDepth={endDepth}
                    byBoreholeDepth={byBoreholeDepth}
                    sortedTrackData={sortedTrackData}
                    template={template}
                    depthUnit={depthUnit}
                    removeTrack={removeTrack}
                    trackTypes={trackTypes}
                    editMode={editMode}
                    depthMajorIntervals={depthMajorIntervals}
                    showDepthGridLines={showDepthGridLines}
                    showDepthMinorIntervals={showDepthMinorIntervals}
                    depthMinorIntervals={depthMinorIntervals}
                    deletedCurves={deletedCurves}
                    selectedTrackCategory={selectedTrackCategory}
                    setSelectedTrackCategory={setSelectedTrackCategory}
                    trackHeaderClick	={trackHeaderClick}
                    setMetaDataDepth={setMetaDataDepth}
                    selectLithology={selectLithology}
                    addLithology={addLithology}
                    topMargin={topMargin}
                    availableHeight={availableHeight}
                    onUpdateCurve={onUpdateCurve}
                    onUpdateCurves={onUpdateCurves}
                    onSetShowGridlines={onSetShowGridlines}
                    onSetScaleType={onSetScaleType}
                    onSetTrackWidth={onSetTrackWidth}
                    headerHeight={headerHeight}
                    minHeaderHeight={minHeaderHeight}
                    headerBottomPadding={headerBottomPadding}
                    windowSize={windowSize}
                    container={container}
                    imageHeight={imageHeight}
                    apiUrl={apiUrl}
                    imageAuthToken={imageAuthToken}                  
                    cumulativeHeaderHeight={cumulativeHeaderHeight}
                    onZoom={onZoom}
                    onPan={onPan}
                    onSetIsPanning={handleSetIsPanning}
                    isPanning={isPanning}
                    mouseWheelZoom={mouseWheelZoom}
                    />              
              </> }
            </div>   
            </>}       
           </div>
           
           {/* High-resolution data loading indicator */}
           {isLoadingHighResolution && (
             <div style={{
               position: 'fixed',
               top: '10px',
               right: '10px',
               backgroundColor: 'rgba(0, 0, 0, 0.8)',
               color: 'white',
               padding: '8px 16px',
               borderRadius: '4px',
               fontSize: '12px',
               zIndex: 9999,
               display: 'flex',
               alignItems: 'center',
               gap: '8px'
             }}>
               <div style={{
                 width: '12px',
                 height: '12px',
                 border: '2px solid rgba(255,255,255,0.3)',
                 borderTopColor: 'white',
                 borderRadius: '50%',
                 animation: 'spin 0.8s linear infinite'
               }}></div>
               Loading high-resolution data...
             </div>
           )}
        </>
    )
  }
  